import * as Tooltip from '@radix-ui/react-tooltip';
import {
  Fragment,
  forwardRef,
  isValidElement,
  useState,
  type ReactNode,
} from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import { useBaseOverlayPortal } from './internal/overlayPortal';
import type { BaseTooltipPlacement, BaseTooltipProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function validateBoolean(value: unknown, name: string) {
  if (typeof value !== 'boolean') {
    throw new TypeError(`BaseTooltip ${name} must be a boolean.`);
  }
}

function validatePlacement(value: unknown) {
  if (
    value !== 'top' &&
    value !== 'bottom' &&
    value !== 'left' &&
    value !== 'right'
  ) {
    throw new TypeError(
      'BaseTooltip placement must be "top", "bottom", "left", or "right".',
    );
  }
}

function normalizeTrigger(children: ReactNode, disabled: boolean) {
  if (isValidElement(children) && children.type !== Fragment) {
    return children;
  }

  return (
    <span tabIndex={!disabled && hasRenderableNode(children) ? 0 : undefined}>
      {children}
    </span>
  );
}

export const BaseTooltip = forwardRef<HTMLSpanElement, BaseTooltipProps>(
  function BaseTooltip(
    {
      content,
      children,
      open,
      defaultOpen = false,
      placement = 'top',
      disabled = false,
      portal = false,
      appearance,
      className,
      classNames,
      style,
      onOpenChange,
      ...tooltipProps
    },
    ref,
  ) {
    if (open !== undefined) validateBoolean(open, 'open');
    validateBoolean(defaultOpen, 'defaultOpen');
    validateBoolean(disabled, 'disabled');
    validateBoolean(portal, 'portal');
    validatePlacement(placement);

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const overlayPortal = useBaseOverlayPortal({ disabled: !portal });
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isControlled = open !== undefined;
    const canShow = !disabled && hasRenderableNode(content);
    const requestedOpen = isControlled ? open : uncontrolledOpen;
    const isOpen = canShow && requestedOpen;
    const portalState =
      portal && overlayPortal.shouldPortal ? 'portaled' : 'inline';

    const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen && !canShow) return;
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    };

    const trigger = normalizeTrigger(children, disabled);
    const tooltipContent = canShow ? (
      <Tooltip.Content
        {...componentAttributes}
        side={placement}
        sideOffset={8}
        collisionPadding={8}
        data-monkeys-component="tooltip"
        data-slot="content"
        data-placement={placement}
        data-portal={portalState}
        data-disabled={disabled || undefined}
        className={cn(theme.slots.tooltipContent, classNames?.content)}
        style={theme.styles.tooltip}
      >
        {content}
      </Tooltip.Content>
    ) : null;

    return (
      <span
        {...componentAttributes}
        {...tooltipProps}
        ref={ref}
        data-monkeys-component="tooltip"
        data-slot="root"
        data-state={isOpen ? 'open' : 'closed'}
        data-placement={placement}
        data-portal={portalState}
        data-disabled={disabled || undefined}
        className={cn(theme.slots.tooltipRoot, classNames?.root, className)}
        style={style}
      >
        <Tooltip.Provider
          delayDuration={0}
          skipDelayDuration={0}
          disableHoverableContent
        >
          <Tooltip.Root
            open={isOpen}
            onOpenChange={handleOpenChange}
            delayDuration={0}
            disableHoverableContent
          >
            <span
              {...componentAttributes}
              data-monkeys-component="tooltip"
              data-slot="trigger-wrapper"
              data-state={isOpen ? 'open' : 'closed'}
              data-placement={placement}
              data-portal={portalState}
              data-disabled={disabled || undefined}
              className={cn(theme.slots.tooltipTrigger, classNames?.trigger)}
            >
              <Tooltip.Trigger
                asChild
                {...componentAttributes}
                data-monkeys-component="tooltip"
                data-slot="trigger"
                data-placement={placement}
                data-portal={portalState}
                data-disabled={disabled || undefined}
              >
                {trigger}
              </Tooltip.Trigger>
            </span>
            {portal && overlayPortal.shouldPortal ? (
              <Tooltip.Portal {...overlayPortal.radixPortalProps}>
                {tooltipContent}
              </Tooltip.Portal>
            ) : (
              tooltipContent
            )}
          </Tooltip.Root>
        </Tooltip.Provider>
      </span>
    );
  },
);
